import React, { useEffect, useMemo, useState } from "react";
import { MdPerson, MdEvent } from "react-icons/md";
import { getEmployeeLoad } from "../../../../services/capacityApi";
import { EmployeeLoadItem } from "../../../../interfaces/Capacity";

interface Props {
  /** Colaboradores selecionados no filtro. */
  employeeIds: number[];
  /** Nome por id (para o cabeçalho). */
  namesById: Map<number, string>;
  /** Início (segunda) e nº de semanas do período visível. */
  start: Date;
  weeks: number;
}

const fmtWeek = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};
const fmtH = (h: number) => {
  const t = Math.round((h || 0) * 60);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CapacityEmployeeTasks: React.FC<Props> = ({ employeeIds, namesById, start, weeks }) => {
  const [items, setItems] = useState<EmployeeLoadItem[]>([]);
  const [loading, setLoading] = useState(false);

  const key = `${employeeIds.join(",")}|${isoDate(start)}|${weeks}`;
  useEffect(() => {
    if (employeeIds.length === 0) {
      setItems([]);
      return;
    }
    setLoading(true);
    Promise.all(employeeIds.map((id) => getEmployeeLoad(id, start, weeks).catch(() => [])))
      .then((lists) => setItems(lists.flat()))
      .catch((e) => console.error("Erro ao carregar detalhamento de capacity", e))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Agrupa por colaborador → semana → tarefas.
  const byEmployee = useMemo(() => {
    const m = new Map<number, EmployeeLoadItem[]>();
    items.forEach((it) => {
      const arr = m.get(it.employeeId) ?? [];
      arr.push(it);
      m.set(it.employeeId, arr);
    });
    return m;
  }, [items]);

  if (employeeIds.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="text-sm font-bold text-navy-700 dark:text-white">
          Tarefas que compõem o capacity
        </h4>
        <p className="text-xs text-gray-700 dark:text-gray-300">
          Detalhamento das horas planejadas por semana e cliente para o(s) colaborador(es) filtrado(s).
        </p>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-700 dark:text-gray-300">Carregando...</div>
      ) : (
        employeeIds.map((empId) => {
          const rows = byEmployee.get(empId) ?? [];
          const total = rows.reduce((a, r) => a + r.plannedHours, 0);

          // semanas ordenadas
          const weeksMap = new Map<string, EmployeeLoadItem[]>();
          rows.forEach((r) => {
            const arr = weeksMap.get(r.weekStart) ?? [];
            arr.push(r);
            weeksMap.set(r.weekStart, arr);
          });
          const weekKeys = Array.from(weeksMap.keys()).sort();

          return (
            <div
              key={empId}
              className="overflow-hidden rounded-xl border border-gray-100 dark:border-white/10"
            >
              <div className="flex items-center justify-between bg-gray-50 px-4 py-2 dark:bg-navy-900">
                <span className="flex items-center gap-1.5 text-sm font-bold text-navy-700 dark:text-white">
                  <MdPerson size={16} aria-hidden="true" className="text-dv-blue-600 dark:text-dv-cyan-300" />
                  {namesById.get(empId) ?? rows[0]?.employeeName ?? `Colaborador #${empId}`}
                </span>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-navy-800 dark:text-gray-300">
                  {fmtH(total)} no período
                </span>
              </div>

              {rows.length === 0 ? (
                <div className="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">Sem tarefas no período.</div>
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-white/5">
                  {weekKeys.map((wk) => {
                    const wkRows = (weeksMap.get(wk) ?? []).sort(
                      (a, b) =>
                        (a.clientName ?? "").localeCompare(b.clientName ?? "") ||
                        b.plannedHours - a.plannedHours
                    );
                    const wkTotal = wkRows.reduce((a, r) => a + r.plannedHours, 0);
                    return (
                      <div key={wk} className="px-4 py-2">
                        <div className="mb-1.5 flex items-center justify-between">
                          <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                            <MdEvent size={13} aria-hidden="true" /> Semana de {fmtWeek(wk)}
                          </span>
                          <span className="text-[11px] font-bold text-dv-blue-600 dark:text-dv-cyan-300">
                            {fmtH(wkTotal)}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          {wkRows.map((r, i) => (
                            <div
                              key={`${r.taskId}-${i}`}
                              className="flex items-center justify-between gap-3 rounded-lg bg-gray-50/60 px-2.5 py-1.5 text-xs dark:bg-navy-900/40"
                            >
                              <div className="min-w-0">
                                <p className="truncate font-medium text-navy-700 dark:text-white">
                                  {r.taskTitle}
                                </p>
                                <p className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                                  {(r.clientName ?? "Sem cliente") + " · " + (r.projectName ?? "Sem projeto")}
                                </p>
                              </div>
                              <span className="shrink-0 rounded-md bg-dv-blue-50 px-1.5 py-0.5 text-[11px] font-bold text-dv-blue-600 dark:bg-dv-blue-500/10 dark:text-dv-cyan-300">
                                {fmtH(r.plannedHours)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default CapacityEmployeeTasks;
